import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneResourcesListing,
} from "@planzone/view-models";
import { ResourcesListingFilter, ResourcesListingSort } from "@planzone/dto";

import { WorkspaceContext } from "@scenes/workspaceContext";
import { usePreferences } from "@components/preferences";
import { Loader } from "@common/loader";
import { ProfileNavigation } from "@common/profileNavigation";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { Props } from "./types";
import { Sidebar } from "./sidebar";
import { Menu } from "./menu";
import { ResourcesList } from "./resourcesList";
import { Toolbar } from "./toolbar";
import { Filter, SortOption } from "./types";
import styles from "./styles.scss";

const PAGE_SIZE = 50;

const listingSorts: { [Key in SortOption]: ResourcesListingSort } = {
  [SortOption.Default]: ResourcesListingSort.Default,
  [SortOption.NAME]: ResourcesListingSort.Name,
  [SortOption.Role]: ResourcesListingSort.Role,
  [SortOption.CreationDate]: ResourcesListingSort.CreationDate,
};

// Filtered, sorted and paginated by the back, like the members page whose listing it shares.
function CostProfilesPage(props: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { state, edit } = usePreferences();
  const { resourcesFilter: filter } = state;
  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, resourcesFilter: nextFilter });
  const {
    resources,
    filter: listingFilter,
    totalPages,
    isRequestPending,
    retrievePlanzoneResourcesListing,
  } = useRetrievePlanzoneResourcesListing();
  const [sort, setSort] = useState<SortOption>(SortOption.Default);
  const listingCriteria: ResourcesListingFilter = {
    page: 0,
    size: PAGE_SIZE,
    resourceTypes: filter.resourceTypes,
    roles: filter.roles,
    resources: filter.resources,
    groups: filter.groups,
    resourceTags: filter.resourceTags,
    sort: listingSorts[sort],
  };

  React.useEffect(() => {
    retrievePlanzoneResourcesListing({ planzone, filter: listingCriteria });
  }, [planzone.uuid, JSON.stringify(listingCriteria)]);

  return (
    <WorkspaceContext type="planzone">
      <div className={styles.costProfilesPage}>
        <div className={styles.header}>
          <h1 className={styles.title}>{t("costProfilesPage.title")}</h1>
          <div className={styles.profileNavigation}>
            <AssistantShortcut />
            <Notifications />
            <ProfileNavigation />
          </div>
        </div>
        <div className={styles.menu}>
          <Toolbar
            sort={sort}
            filter={filter}
            setSort={setSort}
            setFilter={setFilter}
          />
          <Menu />
        </div>
        <div className={styles.main}>
          <div className={styles.sidebar}>
            <Sidebar />
          </div>
          <div className={styles.content}>
            <div className={styles.heading}>
              <p className={styles.label}>{t("costProfilesPage.name")}</p>
              <p className={styles.label}>
                {t("costProfilesPage.affiliation")}
              </p>
              <p className={styles.label}>
                {t("costProfilesPage.costProfile")}
              </p>
              <p className={styles.label}></p>
            </div>
            <div className={styles.resourcesList}>
              {isRequestPending && resources.length === 0 ? (
                <div className={styles.loader}>
                  <div className={styles.loaderMain}>
                    <Loader />
                  </div>
                </div>
              ) : (
                <ResourcesList
                  resources={resources}
                  isRequestPending={isRequestPending}
                  canLoadMore={listingFilter.page < totalPages}
                  retrieveNextResources={() =>
                    retrievePlanzoneResourcesListing({
                      planzone,
                      filter: listingFilter,
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </WorkspaceContext>
  );
}

export { CostProfilesPage };
